import { PaginationParam, PaginationResponse } from "@/app/api/type"
import { ReportStatus } from "./report"
import { PlayerData } from "./player"

import { Session, SessionData } from "./session"
import { PlayerDevice } from "@/types/device"

export enum MatchDayType {
  HOME = "MD(H)",
  AWAY = "MD(A)",
  AFTER_1 = "MD+1",
  AFTER_2 = "MD+2",
  AFTER_3 = "MD+3",
  BEFORE_1 = "MD-1",
  BEFORE_2 = "MD-2",
  BEFORE_3 = "MD-3",
  BEFORE_4 = "MD-4",
  BEFORE_5 = "MD-5",
  BEFORE_6 = "MD-6",
  BEFORE_7 = "MD-7",
  NONE = "None",
}

// 선언 순서 = 타입 나열/정렬 순서 (T, L, C, F, R, E — constants/activity.ts 의 동명 enum 과 통일)
export enum ActivityTypeKey {
  training = "T",
  league = "L",
  cup = "C",
  friendlyMatch = "F",
  rehab = "R",
  etc = "E",
}

export const ActivityType = {
  T: "training",
  L: "league",
  C: "cup",
  F: "friendlyMatch",
  R: "rehab",
  E: "etc",
}

export type ActivityParticipatedPlayerType = {
  playTime: number
  backNumber: number
  uniformNumber: number
  idfPlayer: number
  nickname: string
  name: string
  position: string
  image: string
  idfPlayerGroup: number
  participatedSessions?: number[]
}

export type ActivityParticipatedPlayerKey = keyof ActivityParticipatedPlayerType

export type ActivityParticipatedPlayer = {
  [key in ActivityParticipatedPlayerKey]: ActivityParticipatedPlayerType[key]
}

type Activity = {
  idfActivity?: number
  dateStart: string
  dateFinish: string
  idfStadium: number
  name: string
  type: string
  startToDate: string
  finishToDate: string
  isLive?: boolean
  rpe?: number
  analyzeFinished?: string
  // /activities/recent-overview 는 분석 상태를 status 로 내려준다 (ActivityOverviewDto.status)
  status?: ReportStatus
  avgLongitude?: number
  avgLatitude?: number
  playerCount?: number
  width?: number
  height?: number
  sessionCount?: number
  memo?: string
  matchDay?: MatchDayType
  idfSeasonWeek?: number | null
}

type ActivityDetail = {
  dateStart: string
  dateFinish: string
  idfActivity: number
  name: string
  players: ActivityParticipatedPlayer[]
  sessions: Session[]
  stadium: string
  idfStadium: number
  type: keyof typeof ActivityType
  memo?: string
  matchDay: MatchDayType
  seasonWeek: string
  idfSeasonWeek?: number | null
  analyzeFinished: string
}

type ActivitySearchItem = {
  idfActivity: number
  name: string
  type: string
  dateStart: string
  dateFinish: string
}

type ReadActivitySearchResponse = ActivitySearchItem[]

type ReadActivityListParams = PaginationParam & Partial<Activity>
type ReadActivityListResponse = PaginationResponse<Activity>
type ReadActivityOverviewListParams = PaginationParam & Partial<Activity>
type ReadActivityOverviewListResponse = Activity[]
type CreateActivityPayload = CreateActivityDataPayload
type CreateActivityResponse = number
type ReadActivityResponse = Activity
type UpsertActivityPayload = Activity
type UpsertActivityResponse = Activity

type ActivityData = {
  idfData: number
  idfActivity: number
  idfPlayer: number
  position: string
  deviceModel: string
  timezone: string
  deviceVersion: string
  deviceNumber: number
  dateStart: string
  dateFinish: string
  status: string
  isGps: boolean
  isImu: boolean
  isHr: boolean
  existGps: boolean
  existImu: boolean
  existHr: boolean
  matchedToActivity: boolean
  avgLatitude: number
  avgLongitude: number
  idfTeam: number
  isLive: boolean
  devicePlayer: PlayerDevice
}
type ReadActivityDataResponse = ActivityData[]

type ActivityDataTimelineProps = Omit<ActivityData, "idfData"> & {
  idfData: number[]
  activityDataByIdfData?: Record<number, ActivityData>
}
type TrendReportDataType = {
  name: string
  playerCount: number | string
  activityCount: number | string
  activityKeyCount: {
    [key: string]: number | string
  }
}

type CreateActivityDataPayload = {
  dateStart?: string
  dateFinish?: string
  idfData?: number
  startToDate?: string
  finishToDate?: string
}
type CreateActivityDataResponse = ActivityData

type DeleteActivityDataParams = { idfActivity: number; idfData: number }

type ActivityRpe = {
  idfActivity: number
  idfPlayer: number
  idfData: number
  idfTeam: number
  idfTeamPlayerGroup: number
  playerName: string
  position: string
  playerNumber: number
  rpe?: number | "-"
}

type UpsertActivityTimePayload = {
  dateStart: string
  dateFinish: string
  idfActivity: number
  idfStadium: number
  name: string
  rpe: ActivityRpe[]
}

type ReadRecentActivityOverviewParams = {
  timezone?: string
  date: number
  type?: string[]
  limit?: number
}
type ReadRecentActivityOverviewResponse = Activity[]

type ReadLatestActivityParams = {
  start: number
  end: number
}
type ReadLatestActivityResponse = Pick<Activity, "idfActivity">

type ActivityTypeStatus = {
  [key in keyof ActivityTypeKey]: number
}
type ReadActivityTypeStatusResponse = ActivityTypeStatus
type ReadActivityTypeStatsOfPlayerResponse = ActivityTypeStatus

type ReadActivityTypeStatsOfPlayerParams = Pick<PaginationParam, "start" | "end"> & {
  idfTeam: number
  idfPlayers?: number
}

type ReadAnalyzingActivityResponse = {
  status: string
}

type ActivitiesAnalysisStatus = {
  idfActivity: number
  name: string
  date: string
  playerCount: number
  type: ActivityTypeKey
  status: ReportStatus
  matchDay?: MatchDayType
  idfSeasonWeek?: number | null
}

type ReadActivitiesAnalysisStatusParams = {
  start: number
  end: number
  timezone?: string
}
type ReadActivitiesAnalysisStatusResponse = ActivitiesAnalysisStatus[]

type ActivityPlayerEditList = {
  idfData: number
  idfTeamPlayerGroup: number | null
  position: string | null
  rpe: number | "-" | null
}
type UpsertPlayerRpePayload = {
  activityPlayerEditList: ActivityPlayerEditList[]
}

type ReadPlayerRpeResponse = PlayerData[]
type UpsertPlayerRpeResponse = PlayerData[]

type ReadActivityParticipatedPlayerListParams = number[]
type ReadActivityParticipatedPlayerListResponse = ActivityParticipatedPlayer[]

type ReadSessionListParams = {
  idfActivity: number
  idfTeam?: number
  idfPlayers?: number
}
type ReadSessionListResponse = Session[]

type ReadSessionDataItemsInActivityResponse = SessionData[]
type ReadActivityDetailResponse = ActivityDetail

type TransformedActivityDataType = Omit<ActivityData, "dateStart" | "dateFinish"> & {
  serial: string
  dateStart: Date
  dateFinish: Date
  existGps: boolean
  existImu: boolean
  existHr: boolean
}

type TransformedActivityDataKey = keyof TransformedActivityDataType

type TransformedActivityData = {
  [key in TransformedActivityDataKey]: TransformedActivityDataType[key]
}

type PlayerDeviceInActivity = Pick<
  PlayerDevice,
  | "idfTeam"
  | "idfPlayer"
  | "uniformNumber"
  | "deviceModel"
  | "deviceVersion"
  | "deviceNumber"
  | "serialNumber"
  | "nickname"
>

type ReadActivityPlayerDeviceListResponse = PlayerDeviceInActivity[]

export type {
  Activity,
  ReadActivityListParams,
  ReadActivityListResponse,
  ReadActivityOverviewListParams,
  ReadActivityOverviewListResponse,
  CreateActivityPayload,
  CreateActivityResponse,
  ReadActivityResponse,
  ActivityData,
  ActivitiesAnalysisStatus,
  ActivityDetail,
  ActivityDataTimelineProps,
  ActivitySearchItem,
  ReadActivityDataResponse,
  CreateActivityDataPayload,
  CreateActivityDataResponse,
  DeleteActivityDataParams,
  UpsertActivityPayload,
  UpsertActivityResponse,
  UpsertActivityTimePayload,
  ReadRecentActivityOverviewParams,
  ReadRecentActivityOverviewResponse,
  ReadLatestActivityParams,
  ReadLatestActivityResponse,
  ReadActivityTypeStatusResponse,
  ReadActivityTypeStatsOfPlayerParams,
  ReadActivityTypeStatsOfPlayerResponse,
  ReadAnalyzingActivityResponse,
  ReadActivitiesAnalysisStatusParams,
  ReadActivitiesAnalysisStatusResponse,
  UpsertPlayerRpePayload,
  ReadPlayerRpeResponse,
  UpsertPlayerRpeResponse,
  ReadActivityParticipatedPlayerListParams,
  ReadActivityParticipatedPlayerListResponse,
  ReadSessionListParams,
  ReadSessionListResponse,
  ReadSessionDataItemsInActivityResponse,
  ReadActivityDetailResponse,
  TransformedActivityData,
  ReadActivitySearchResponse,
  PlayerDeviceInActivity,
  ReadActivityPlayerDeviceListResponse,
  TrendReportDataType,
}
